import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import AdminNavigation from './AdminNavigation';
import { InfinityLoader } from '../../components/InfinityLoader';

export function AdminProtectedRoute({ children, permission }) {
  const {
    authError,
    isAuthenticated,
    loading,
    hasPermission,
    requirePasswordChange,
  } = useAdminAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <InfinityLoader size={96} />
      </div>
    );
  }

  if (authError) {
    return <AdminAccessRequired reason={authError} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (requirePasswordChange && window.location.pathname !== '/admin/change-password') {
    return <Navigate to="/admin/change-password" replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <AdminAccessRequired />;
  }

  return children;
}

function AdminAccessRequired({ reason }) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div
        className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-slate-900 p-8 text-center"
        data-testid="admin-access-required"
      >
        <h1 className="text-xl font-semibold text-white">Admin access required.</h1>
        <p className="mt-2 text-sm text-slate-400">
          {reason || 'Sign in with an authorized administrator account to use this area.'}
        </p>
        <a
          href="/admin/login"
          className="mt-6 inline-flex rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
        >
          Return to admin login
        </a>
      </div>
    </div>
  );
}

export function AdminLayout({ children }) {
  return (
    <div className="everbook-admin-shell admin-surface-page min-h-screen">
      <AdminNavigation />
      <main className="min-w-0 lg:pl-64 pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
